/**
 * Token Expiration Service
 * Manages session expiration tracking for 3-hour login sessions
 */

const SESSION_DURATION = 3 * 60 * 60 * 1000; // 3 hours in milliseconds
const TOKEN_TIMESTAMP_KEY = 'tokenTimestamp';

class TokenExpirationService {
  /**
   * Store the current timestamp when user logs in
   */
  static setLoginTimestamp(): void {
    const timestamp = Date.now();
    localStorage.setItem(TOKEN_TIMESTAMP_KEY, timestamp.toString());
  }

  /**
   * Get the stored login timestamp
   */
  static getLoginTimestamp(): number | null {
    const timestamp = localStorage.getItem(TOKEN_TIMESTAMP_KEY);
    return timestamp ? parseInt(timestamp, 10) : null;
  }

  /**
   * Check if the current session has expired
   * @returns true if session is expired, false otherwise
   */
  static isSessionExpired(): boolean {
    const timestamp = this.getLoginTimestamp();
    
    if (!timestamp) {
      // No timestamp means no active session
      return true;
    }

    const currentTime = Date.now();
    const elapsedTime = currentTime - timestamp;

    return elapsedTime >= SESSION_DURATION;
  }

  /**
   * Get remaining session time in milliseconds
   * @returns remaining time in ms, or 0 if expired
   */
  static getRemainingTime(): number {
    const timestamp = this.getLoginTimestamp();
    
    if (!timestamp) {
      return 0;
    }

    const currentTime = Date.now();
    const elapsedTime = currentTime - timestamp;
    const remainingTime = SESSION_DURATION - elapsedTime;

    return remainingTime > 0 ? remainingTime : 0;
  }

  /**
   * Get remaining session time in a human-readable format
   * @returns formatted string like "2h 45m"
   */
  static getRemainingTimeFormatted(): string {
    const remaining = this.getRemainingTime();
    
    if (remaining <= 0) {
      return 'Expired';
    }

    const hours = Math.floor(remaining / (60 * 60 * 1000));
    const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }

  /**
   * Clear the login timestamp
   */
  static clearLoginTimestamp(): void {
    localStorage.removeItem(TOKEN_TIMESTAMP_KEY);
  }

  /**
   * Validate if token and session are still valid
   * @returns true if valid, false if expired or missing
   */
  static isSessionValid(): boolean {
    const token = localStorage.getItem('authToken');
    
    if (!token) {
      return false;
    }

    return !this.isSessionExpired();
  }

  /**
   * Set up a timer to automatically logout when session expires
   * @param logoutCallback - Function to call when session expires
   * @returns timer ID that can be used to clear the timer
   */
  static setupExpirationTimer(logoutCallback: () => void): NodeJS.Timeout | null {
    const remainingTime = this.getRemainingTime();
    
    if (remainingTime <= 0) {
      // Session already expired
      logoutCallback();
      return null;
    }

    // Set timeout for remaining time
    const timerId = setTimeout(() => {
      logoutCallback();
    }, remainingTime);

    return timerId;
  }
}

export default TokenExpirationService;

